/* eslint-disable @nx/enforce-module-boundaries */
import {
  Avatar,
  Box,
  Button,
  Divider,
  HStack,
  Heading,
  Link,
  LinkButton,
  SeeMore,
  Stack,
  Text,
  IconButton,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  VStack,
} from '@mybridge/ui';
import moment from 'moment';
import React, { forwardRef, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EMPLOYMENT_CHOICES } from 'v4/lib/data';
import { MyProfileContext } from '../context';
import { ShowAllLink } from '../show-all-link';
import { formatCommaSeparated } from 'v4/lib/commons';
import {
  AddCircle,
  EarthGlobeIcon,
  TeamIcon,
  OnlyMeLockIcon,
  AddCircleFIlled,
} from '@mybridge/icons';
import { EditIcon } from '@mybridge/icons/Edit';
import { MyProfileEducationModal } from '../education-modal';
import { AddIcon } from '@chakra-ui/icons';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useRouter } from 'next/router';

export const MyProfileEducation = React.memo(forwardRef(({ ...props }, ref) => {
  const {
    userProfileInfo,
    educationDisc,
    myEducation,
    refetchMyEducation,
    setSelectedEducation,
    entityHasStartDates,
    entityHasEndDates,
    audienceDisc,
    setPermissionValue,
    setPermissionId,
    type,
    setType,
    setCurrentPermissionValue
  } = useContext(MyProfileContext);
  const { t } = useTranslator();
  const [showAll, setShowAll] = useState(false);
  const router = useRouter();
  const sectionRef = useRef(null);

  useEffect(() => {
    // Check if we should open the education modal based on query param
    if (router.query.openDisc === 'education') {
      setSelectedEducation(null);
      educationDisc?.onOpen?.();
      
      // Set type if specified
      if (router.query.type) {
        setType(router.query.type);
      }
      
      // Clear the query parameter after opening the disclosure
      router.replace(router.pathname, undefined, { shallow: true });
    }
    
    // Check if we should scroll to this section
    if (router.query.scrollTo === 'education' && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Clear the scroll parameter after scrolling
      const { scrollTo, ...rest } = router.query;
      router.replace(
        { pathname: router.pathname, query: rest },
        undefined,
        { shallow: true }
      );
    }
  }, [router.query, educationDisc, setSelectedEducation, setType]);

  useEffect(() => {
    refetchMyEducation?.();
  }, []);

  const onEditClick = (edu) => (e) => {
    setSelectedEducation(edu);
    educationDisc?.onOpen?.();
  };

  const schools = useMemo(
    () => myEducation?.filter((me) => me?.type == 'School') ?? [],
    [myEducation]
  );
  const colleges = useMemo(
    () => myEducation?.filter((me) => me?.type !== 'School') ?? [],
    [myEducation]
  );

  const groupedSchools = useMemo(() => {
    const myExps = {};
    if (!Array.isArray(schools)) return;
    for (let me of schools) {
      if (myExps?.[me?.name]) {
        myExps[me.name].push(me);
      } else {
        myExps[me.name] = [me];
      }
    }
    return myExps;
  }, [schools]);
  const groupedColleges = useMemo(() => {
    const myExps = {};
    if (!Array.isArray(colleges)) return;
    for (let me of colleges) {
      if (myExps?.[me?.name]) {
        myExps[me.name].push(me);
      } else {
        myExps[me.name] = [me];
      }
    }
    return myExps;
  }, [colleges]);

  const renderPermissionSwitch = (param) => {
    switch (param) {
      case 1:
        return {
          icon: <EarthGlobeIcon width="16px" height="16px" />,
          text: t('profileDetails.public') || 'Public',
        };
      case 2:
        return {
          icon: <TeamIcon width="16px" height="16px" />,
          text: t('profileDetails.myNetwork') || 'myNetwork',
        };
      case 3:
        return {
          icon: <OnlyMeLockIcon width="16px" height="16px" />,
          text: t('profileDetails.onlyMe') || 'Only me',
        };
      default:
        return {
          icon: <EarthGlobeIcon width="16px" height="16px" />,
          text: t('profileDetails.public') || 'Public',
        };
    }
  };

  const Item = React.memo(({
    exp,
    hasBar = false,
    isLast = false,
    hasTitle = false,
    isSchool = false,
  }) => {
    return (
      <Stack spacing={0} pt={hasTitle ? 4 : 0} pos="relative">
        {hasBar ? (
          <>
            <Box
              pos="absolute"
              w="20px"
              h="20px"
              top={4}
              left={'-40px'}
              bg="brandGray.300"
              borderRadius="full"
              zIndex={1}
            />
            <Box
              pos="absolute"
              w="1"
              top={4}
              bottom={-4}
              left={-8}
              bg="brandGray.300"
            />
          </>
        ) : isLast ? (
          <Box
            pos="absolute"
            w="20px"
            h="20px"
            top={4}
            left={'-40px'}
            bg="brandGray.300"
            borderRadius="full"
            zIndex={1}
          />
        ) : (
          ''
        )}
        <HStack alignItems="flex-start">
          <Stack spacing={0} flex={1}>
            {hasTitle && exp?.degree && !isSchool ? (
              <Text>
                Studied {exp?.degree} at {exp?.name}
              </Text>
            ) : (
              ''
            )}

            <Box fontSize="sm">
              {entityHasStartDates(exp) || entityHasEndDates(exp) ? (
                <Text display="inline">
                  {[
                    // Part 1: Start - End
                    [
                      moment()
                        .set('M', exp?.start_month - 1)
                        .set('Y', exp?.start_year)
                        .format('MMMM YYYY'),
                      exp?.end_month && exp?.end_year
                        ? moment()
                            .set('M', exp?.end_month - 1)
                            .set('Y', exp?.end_year)
                            .format('MMMM YYYY')
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' - '),

                    // Part 2: Graduated (only if true)
                    exp?.is_graduate ? (t('education.graduated') || 'Graduated') : null,
                  ]
                    .filter(Boolean)
                    .join(' | ')}
                </Text>
              ) : (
                ''
              )}
              {exp?.location?.length ? (
                <>
                  <Text display="inline">&nbsp;·&nbsp;</Text>
                  <Text display="inline">{exp?.location}</Text>
                </>
              ) : (
                ''
              )}
            </Box>
            <Box fontSize="sm">
              {formatCommaSeparated(
                [
                  exp?.industry,
                  EMPLOYMENT_CHOICES?.find(
                    (ec) => ec.value === exp?.employment_type
                  )?.label,
                ],
                ' · '
              )}
            </Box>
            <Stack py={2}>
              {exp?.desc ? <SeeMore text={exp?.desc} /> : ''}
            </Stack>
          </Stack>
          {hasTitle ? (
            // <LinkButton onClick={onEditClick(exp)}>Edit</LinkButton>
            <HStack>
              <Tooltip label={renderPermissionSwitch(exp?.audience)?.text}>
                <IconButton
                  icon={renderPermissionSwitch(exp?.audience)?.icon}
                  size="sm"
                  variant="transparent"
                  onClick={() => {
                    audienceDisc.onOpen();
                    setPermissionValue('education');
                    setCurrentPermissionValue(exp?.audience)
                    setPermissionId(exp?.id);
                  }}
                />
              </Tooltip>
              <Tooltip label={t('buttons.edit') || "Edit"}>
                <IconButton
                  icon={<EditIcon width="16px" height="16px" />}
                  size="sm"
                  variant="transparent"
                  onClick={onEditClick(exp)}
                />
              </Tooltip>
            </HStack>
          ) : (
            ''
          )}
        </HStack>
      </Stack>
    );
  });

  const ItemSchool = useMemo(
    () => React.memo(({ edu, eduIndex }) => {
      return (
        <HStack key={eduIndex} alignItems="flex-start">
          <Avatar size="md" name={edu?.name} />
          <Stack spacing={0} flex={1}>
            <Box>
              <Heading size="sm" fontWeight="400">
                {edu?.degree ? edu?.degree : ''} {edu?.degree ? 'at' : ''}{' '}
                {edu?.name}
              </Heading>
            </Box>
            {/* {edu?.degree ? (
              <Box>
                <Text display="inline">{edu?.degree}</Text>{' '}
              </Box>
            ) : (
              ''
            )} */}
            {edu?.courses ? (
              <Box>
                <Text display="inline">{edu?.courses}</Text>{' '}
              </Box>
            ) : (
              ''
            )}
            <Box fontSize="sm">
              {entityHasStartDates(edu) || entityHasEndDates(edu) ? (
                <Text display="inline">
                  {formatCommaSeparated(
                    [
                      moment()
                        .set('M', edu?.start_month - 1)
                        .set('Y', edu?.start_year)
                        .format('MMMM YYYY'),
                        edu?.is_graduate
                        ? t('education.graduated') || 'Graduated'
                      : moment()
                        .set('M', edu?.end_month - 1)
                        .set('Y', edu?.end_year)
                        .format('MMMM YYYY'),
                    ],
                    edu?.is_graduate ? ' | ' : ' - '
                  )}
                </Text>
              ) : (
                ''
              )}
              {edu?.location?.length ? (
                <>
                  <Text fontWeight="600" display="inline">
                    &nbsp;·&nbsp;
                  </Text>
                  <Text fontWeight="600" display="inline">
                    {edu?.location}
                  </Text>
                </>
              ) : (
                ''
              )}
            </Box>
            <Box fontSize="sm">
              <Text display="inline">
                {formatCommaSeparated(
                  [
                    edu?.industry,
                    EMPLOYMENT_CHOICES?.find(
                      (ec) => ec.value === edu?.employment_type
                    )?.label,
                  ],
                  ' · '
                )}
              </Text>
            </Box>
            <Stack py={2}>
              {edu?.desc ? <SeeMore text={edu?.desc} /> : ''}
            </Stack>
          </Stack>
          <Stack>
            <HStack>
              <Tooltip label={renderPermissionSwitch(edu?.audience)?.text}>
                <IconButton
                  icon={renderPermissionSwitch(edu?.audience)?.icon}
                  size="sm"
                  variant="transparent"
                  onClick={() => {
                    audienceDisc.onOpen();
                    setPermissionValue('education');
                    setCurrentPermissionValue(edu?.audience)
                    setPermissionId(edu?.id);
                  }}
                />
              </Tooltip>
              <Tooltip label={t('buttons.edit') || "Edit"}>
                <IconButton
                  icon={<EditIcon width="16px" height="16px" />}
                  size="sm"
                  variant="transparent"
                  onClick={onEditClick(edu)}
                />
              </Tooltip>
            </HStack>
          </Stack>
        </HStack>
      );
    }),
    [onEditClick, EMPLOYMENT_CHOICES]
  );

  return (
    <>
      {educationDisc?.isOpen ? (
        <MyProfileEducationModal />
      ) : (
        <Stack ref={sectionRef}>
          <HStack alignItems="flex-start">
            <Stack flex={1}>
              <Heading
                fontSize="18px"
                color="brandPrimary.500"
                fontWeight="400"
              >
                {t('education.education') || 'Education'}
              </Heading>
            </Stack>
            <HStack justifyContent="flex-end">
              {myEducation?.length && (
                <Popover>
                  <PopoverTrigger>
                    <IconButton
                      icon={<AddIcon color="#868585" />}
                      size={['sm', 'md']}
                      variant="transparent"
                      color="brandPrimary.500"
                    // onClick={(e) => {
                    //   setSelectedEducation(null);
                    //   educationDisc?.onOpen?.();
                    // }}
                    />
                  </PopoverTrigger>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverBody>
                      <VStack alignItems="flex-start">
                        <Text
                          color="brandPrimary.500"
                          fontSize="18px"
                          fontWeight="bold"
                        >
                          {t('education.university') || 'University'}
                        </Text>
                        <Button
                          leftIcon={<AddCircleFIlled />}
                          variant="transparent"
                          color="brandPrimary.500"
                          pl={0}
                          onClick={(e) => {
                            setSelectedEducation();
                            educationDisc?.onOpen?.();
                            setType('College');
                          }}
                        >
                          {t('education.addUniversity') || 'Add university'}
                        </Button>
                        <Text
                          color="brandPrimary.500"
                          fontSize="18px"
                          fontWeight="bold"
                        >
                          {t('education.highSchool') || 'High school'}
                        </Text>
                        <Button
                          leftIcon={<AddCircleFIlled />}
                          variant="transparent"
                          color="brandPrimary.500"
                          pl={0}
                          onClick={(e) => {
                            setSelectedEducation();
                            educationDisc?.onOpen?.();
                            setType('School');
                          }}
                        >
                          {t('education.addSecondarySchool') || 'Add secondary school'}
                        </Button>
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              )}
            </HStack>
          </HStack>
          {/* <pre>{JSON.stringify(myEducation, null, 2)}</pre> */}
          {myEducation?.length ? (
            <>
              {colleges?.length ? (
                <>
                  <Heading color="brandPrimary.500" size="sm" fontWeight="400">
                    {t('education.college') || 'College'}
                  </Heading>
                  <Stack pt={4} spacing={6}>
                    {Object.values(groupedColleges)
                      ?.filter?.((_, ind) => showAll || ind < 3)
                      ?.map?.((expArr, expIndex) => {
                        const exp = expArr?.[0];
                        return (
                          <Stack key={expIndex}>
                            <HStack key={expIndex} alignItems="flex-start">
                              <Avatar size="md" name={exp?.name} />
                              <Stack spacing={0} flex={1}>
                                <Box>
                                  {expArr?.length <= 1 ? (
                                    <Text display="inline">
                                      {t('education.studied') || 'Studied'} {exp?.degree ? exp?.degree : ''}
                                      {" "}
                                      {expArr?.length <= 1 ? (t('education.at') || 'at') + " " : ''}
                                    </Text>
                                  ) : (
                                    ''
                                  )}
                                  <Text display="inline" fontWeight="600">
                                    {exp?.name}
                                  </Text>
                                  {expArr?.length > 1 ? (
                                    <Text fontSize="sm">{t('education.university') || 'University'}</Text>
                                  ) : (
                                    ''
                                  )}
                                </Box>
                                {expArr.length <= 1 ? (
                                  <Item exp={exp} key={expIndex} />
                                ) : (
                                  <Stack spacing={0} mt={6}>
                                    {expArr?.map((exp_, expIndex_) => (
                                      <Item
                                        hasTitle={true}
                                        hasBar={expIndex_ < expArr.length - 1}
                                        exp={exp_}
                                        key={expIndex_}
                                        isLast={expIndex_ === expArr.length - 1}
                                      />
                                    ))}
                                  </Stack>
                                )}
                              </Stack>
                              {expArr.length <= 1 ? (
                                // <Stack>
                                //   <LinkButton onClick={onEditClick(exp)}>
                                //     Edit
                                //   </LinkButton>
                                // </Stack>
                                <HStack>
                                  <Tooltip
                                    label={
                                      renderPermissionSwitch(exp?.audience)
                                        ?.text
                                    }
                                  >
                                    <IconButton
                                      icon={
                                        renderPermissionSwitch(exp?.audience)
                                          ?.icon
                                      }
                                      size="sm"
                                      variant="transparent"
                                      onClick={() => {
                                        audienceDisc.onOpen();
                                        setPermissionValue('education');
                                        setCurrentPermissionValue(exp?.audience)
                                        setPermissionId(exp?.id);
                                      }}
                                    />
                                  </Tooltip>
                                  <Tooltip label={t('buttons.edit') || "Edit"}>
                                    <IconButton
                                      icon={
                                        <EditIcon width="16px" height="16px" />
                                      }
                                      size="sm"
                                      variant="transparent"
                                      onClick={onEditClick(exp)}
                                    />
                                  </Tooltip>
                                </HStack>
                              ) : (
                                ''
                              )}
                            </HStack>
                            {Object.values(groupedColleges ?? {})?.length > 1 &&
                              expIndex <
                              Object.values(groupedColleges ?? {})?.length -
                              1 ? (
                              <Divider />
                            ) : (
                              ''
                            )}
                          </Stack>
                        );
                      })}
                  </Stack>
                </>
              ) : (
                ''
              )}
              {schools?.length ? (
                <>
                  <Divider />
                  <Heading
                    mt={4}
                    color="brandPrimary.500"
                    size="sm"
                    fontWeight="400"
                  >
                    {t('education.school') || 'School'}
                  </Heading>
                  <Stack pt={4} spacing={6}>
                    {schools
                      ?.filter?.((_, ind) => showAll || ind < 3)
                      ?.map?.((edu, eduIndex) => (
                        <Stack key={eduIndex}>
                          <ItemSchool edu={edu} eduIndex={eduIndex} />
                          {schools?.length > 1 &&
                            eduIndex < schools?.length - 1 ? (
                            <Divider />
                          ) : (
                            ''
                          )}
                        </Stack>
                      ))}
                  </Stack>
                </>
              ) : (
                ''
              )}
              {!showAll && myEducation?.length > 3 ? (
                <ShowAllLink onClick={(e) => setShowAll(true)} />
              ) : (
                ''
              )}
            </>
          ) : (
            <>
              <HStack justifyContent="flex-start">
                <Popover>
                  <PopoverTrigger>
                    <Button
                      leftIcon={<AddCircleFIlled />}
                      variant="transparent"
                      color="brandPrimary.500"
                      pl={0}
                    // onClick={(e) => {
                    //   setSelectedEducation(null);
                    //   educationDisc?.onOpen?.();
                    // }}
                    >
                      <Box opacity={0.5}>
                        {t('education.addEducation') || 'Add education'}
                      </Box>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverBody>
                      <VStack alignItems="flex-start">
                        <Text
                          color="brandPrimary.500"
                          fontSize="18px"
                          fontWeight="bold"
                        >
                          {t('education.university') || 'University'}
                        </Text>
                        <Button
                          leftIcon={<AddCircleFIlled />}
                          variant="transparent"
                          color="brandPrimary.500"
                          pl={0}
                          onClick={(e) => {
                            setSelectedEducation();
                            educationDisc?.onOpen?.();
                            setType('College');
                          }}
                        >
                          {t('education.addUniversity') || 'Add university'}
                        </Button>
                        <Text
                          color="brandPrimary.500"
                          fontSize="18px"
                          fontWeight="bold"
                        >
                          {t('education.highSchool') || 'High school'}
                        </Text>
                        <Button
                          leftIcon={<AddCircleFIlled />}
                          variant="transparent"
                          color="brandPrimary.500"
                          pl={0}
                          onClick={(e) => {
                            setSelectedEducation();
                            educationDisc?.onOpen?.();
                            setType('School');
                          }}
                        >
                          {t('education.addSecondarySchool') || 'Add secondary school'}
                        </Button>
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </HStack>
            </>
          )}
        </Stack>
      )}
    </>
  );
}));
